import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Instagram, KeyRound, MapPin, ShieldCheck, Sparkles, Home as HomeIcon } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { useSite } from "@/lib/site";
import { ZONES } from "@/lib/constants";
import SearchBar from "@/components/site/SearchBar";
import PropertyCard from "@/components/site/PropertyCard";
import AdSlot from "@/components/site/AdSlot";
import Seo from "@/components/site/Seo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const icons = [KeyRound, MapPin, ShieldCheck, Sparkles];

function SectionHead({ eyebrow, title, text, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
      <div className="max-w-2xl space-y-3">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>
        {text && <p className="text-slate-600 leading-relaxed">{text}</p>}
      </div>
      {action}
    </div>
  );
}

function Hero({ s }) {
  return (
    <section className="relative min-h-[640px] lg:min-h-[720px] flex items-end" data-testid="home-hero">
      <img src={s.hero_image || "/images/hero.jpg"} alt="Punta Cana, República Dominicana" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-900/50 to-transparent" />
      <div className="relative container-x w-full pb-14 pt-32 space-y-8">
        <div className="max-w-3xl space-y-5 fade-up">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-white/80">Punta Cana · Bávaro · Cap Cana</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-white" data-testid="hero-title">{s.hero_title || "Encuentra tu próximo hogar en Punta Cana"}</h1>
          <p className="text-base md:text-lg text-white/85 max-w-xl" data-testid="hero-subtitle">{s.hero_subtitle || "Especialistas en alquileres de largo plazo en Punta Cana, Bávaro y Cap Cana"}</p>
        </div>
        <div className="fade-up" style={{ animationDelay: "150ms" }}><SearchBar /></div>
      </div>
    </section>
  );
}

function Featured() {
  const [data, setData] = useState({ items: [], pages: 1 });
  const [page, setPage] = useState(1);
  useEffect(() => {
    api.get("/public/properties", { params: { limit: 6, page } }).then((r) => setData(r.data));
  }, [page]);
  const go = (n, e) => {
    e?.currentTarget?.blur();
    setPage(n);
    setTimeout(() => {
      const el = document.getElementById("destacadas");
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
    }, 50);
  };
  const items = data.items;
  return (
    <section id="destacadas" className="container-x py-20 sm:py-24 scroll-mt-20" data-testid="featured-section">
      <SectionHead eyebrow="Catálogo" title="Propiedades destacadas" text="Alquileres residenciales de largo plazo seleccionados en Punta Cana."
        action={<Link to="/propiedades" className="inline-flex items-center gap-2 font-semibold text-slate-900 hover:text-[#FF3333]" data-testid="featured-see-all">Ver todas <ArrowRight size={16} /></Link>} />
      <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">{items.map((p) => <PropertyCard key={p.code} p={p} />)}</div>
      {!items.length && <p className="text-slate-500" data-testid="featured-empty">Pronto publicaremos nuevas propiedades.</p>}
      {data.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-12" data-testid="home-pagination">
          <button disabled={page === 1} onClick={(e) => go(page - 1, e)} className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold disabled:opacity-40" data-testid="home-page-prev">Anterior</button>
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
            <button key={n} onClick={(e) => go(n, e)} className={`h-10 w-10 rounded-full text-sm font-semibold ${n === page ? "bg-[#FF3333] text-white" : "bg-white border border-slate-200"}`} data-testid={`home-page-${n}`}>{n}</button>
          ))}
          <button disabled={page === data.pages} onClick={(e) => go(page + 1, e)} className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold disabled:opacity-40" data-testid="home-page-next">Siguiente</button>
        </div>
      )}
    </section>
  );
}

function Zones() {
  const [zones, setZones] = useState([]);
  useEffect(() => { api.get("/public/zones").then((r) => setZones(r.data)); }, []);
  const list = zones.length ? zones : ZONES;
  return (
    <section id="zonas" className="bg-white py-20 sm:py-24 scroll-mt-20" data-testid="zones-section">
      <div className="container-x">
        <SectionHead eyebrow="Zonas" title="Explora las seis zonas de Punta Cana" text="Conoce el estilo de vida de cada zona antes de elegir tu próximo hogar: qué hacer, colegios, plazas cercanas, crecimiento y lo que más valoran quienes ya viven allí." />
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((z, i) => (
            <Link key={z.slug} to={`/zonas/${z.slug}`} className={`group relative overflow-hidden rounded-2xl ${i === 0 || i === 3 ? "lg:row-span-1 aspect-[4/3]" : "aspect-[4/3]"}`} data-testid={`zone-card-${z.slug}`}>
              <img src={z.hero_image || `/images/${z.slug}.jpg`} alt={z.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <h3 className="text-xl font-bold">{z.name}</h3>
                {(z.tagline || z.description) && <p className="text-sm text-white/85 line-clamp-2 mt-1">{z.tagline || z.description}</p>}
                {z.highlights?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{z.highlights.slice(0, 3).map((h) => <span key={h} className="rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-2.5 py-0.5 text-xs">{h}</span>)}</div>}
                <span className="inline-flex items-center gap-1 text-sm font-semibold mt-3 group-hover:gap-2 transition-[gap]">Ver guía <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Owners({ s }) {
  return (
    <section className="container-x py-20 sm:py-24" data-testid="owners-section">
      <div className="relative overflow-hidden rounded-3xl bg-[#121316] text-white grid lg:grid-cols-2">
        <div className="p-10 sm:p-14 space-y-6 relative z-10">
          <p className="eyebrow">Propietarios</p>
          <h2 className="text-3xl lg:text-4xl font-bold">{s.owners_title}</h2>
          <p className="text-slate-300 leading-relaxed max-w-md">{s.owners_text}</p>
          <Link to="/publica-tu-propiedad" className="inline-flex items-center gap-2 rounded-full bg-[#FF3333] hover:bg-[#E62E2E] px-7 py-3.5 font-semibold transition-colors" data-testid="owners-cta-button">Soy Propietario <ArrowRight size={16} /></Link>
        </div>
        <div className="relative min-h-[280px]"><img src="/images/owners.jpg" alt="Propiedad en Punta Cana" className="absolute inset-0 w-full h-full object-cover" /></div>
      </div>
    </section>
  );
}

function Steps({ s }) {
  return (
    <section className="bg-white py-20 sm:py-24" data-testid="process-section">
      <div className="container-x">
        <SectionHead eyebrow="Proceso" title={s.process_title} />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(s.process_steps || []).map((st, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-7 space-y-3 hover:border-[#FF3333]/40 transition-colors">
              <span className="font-mono-num text-sm text-[#FF3333]">0{i + 1}</span>
              <h3 className="text-lg font-semibold">{st.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{st.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Benefits({ s }) {
  return (
    <section className="container-x py-20 sm:py-24" data-testid="benefits-section">
      <SectionHead eyebrow="Por qué SoloRentaPC" title="Beneficios de alquilar con nosotros" />
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {(s.benefits || []).map((b, i) => {
          const Icon = icons[i % icons.length];
          return (
            <div key={i} className="space-y-3">
              <div className="h-12 w-12 rounded-xl bg-red-50 text-[#FF3333] flex items-center justify-center"><Icon size={22} /></div>
              <h3 className="font-semibold text-lg">{b.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Guide({ s }) {
  return (
    <section id="guia" className="bg-white py-20 sm:py-24 scroll-mt-20" data-testid="guide-section">
      <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
        <img src="/images/guide.jpg" alt="Vivir en Punta Cana" className="rounded-3xl w-full aspect-[4/3] object-cover" />
        <div className="space-y-8">
          <div className="space-y-3"><p className="eyebrow">Guía</p><h2 className="text-3xl lg:text-4xl font-bold">{s.guide_title}</h2></div>
          {(s.guide_items || []).map((g, i) => (
            <div key={i} className="flex gap-4">
              <HomeIcon className="text-[#FF3333] shrink-0 mt-1" size={20} />
              <div><h3 className="font-semibold">{g.title}</h3><p className="text-sm text-slate-600 leading-relaxed">{g.text}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq({ s }) {
  return (
    <section className="container-x py-20 sm:py-24 grid lg:grid-cols-[1fr_1.4fr] gap-12" data-testid="faq-section">
      <SectionHead eyebrow="FAQ" title="Preguntas frecuentes" />
      <Accordion type="single" collapsible>
        {(s.faqs || []).map((f, i) => (
          <AccordionItem key={i} value={`f${i}`}>
            <AccordionTrigger className="text-left font-semibold" data-testid={`faq-item-${i}`}>{f.q}</AccordionTrigger>
            <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function Contact({ s }) {
  return (
    <section id="contacto" className="container-x pb-8 scroll-mt-24" data-testid="contact-section">
      <div className="rounded-3xl bg-[#FF3333] text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="space-y-3 max-w-xl"><h2 className="text-3xl lg:text-4xl font-bold">{s.contact_title}</h2><p className="text-white/90">{s.contact_text}</p></div>
        <div className="flex flex-wrap gap-3">
          <a href={waLink(s.whatsapp, "Hola SoloRentaPC, quiero información sobre alquileres.")} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 font-semibold px-6 py-3.5" data-testid="contact-whatsapp-button"><MessageCircle size={18} />WhatsApp</a>
          {s.instagram_url && <a href={s.instagram_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/60 font-semibold px-6 py-3.5" data-testid="contact-instagram-button"><Instagram size={18} />{s.instagram}</a>}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings: s } = useSite();
  return (
    <div data-testid="home-page">
      <Seo description="Alquileres residenciales de largo plazo en Punta Cana, Bávaro, Cap Cana, Vista Cana, Punta Cana Village y Downtown Punta Cana." />
      <Hero s={s} />
      <Featured />
      <Zones />
      <Owners s={s} />
      <Steps s={s} />
      <Benefits s={s} />
      <Guide s={s} />
      <section className="container-x py-20 sm:py-24 space-y-8" data-testid="services-section">
        <SectionHead eyebrow="Servicios recomendados" title="Todo para tu mudanza" text="Proveedores que anuncian en SoloRentaPC. Los anuncios se identifican como patrocinados."
          action={<Link to="/servicios" className="inline-flex items-center gap-2 font-semibold hover:text-[#FF3333]" data-testid="services-see-all">Ver directorio <ArrowRight size={16} /></Link>} />
        <AdSlot placement="home_banner" testid="home-ads" />
      </section>
      <Faq s={s} />
      <Contact s={s} />
    </div>
  );
}
